import { tenantFlagKeys } from '@features/feature-flags';
import { ApiError, UnauthenticatedError } from '@lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    activateComponent,
    createComponent,
    deactivateComponent,
    getComponentSettings,
    getTenantComponents,
    listComponents,
    type ActivationResult,
    type CreateComponentInput,
} from '../api/components';

/**
 * Server state for the component catalog.
 *
 * Its own key space rather than a branch of `flagKeys`, even though a component
 * is made of flags: the rows come from a different partition, a different route
 * and a different tier, and nothing in one can be derived from the other. What
 * a component *contains* is a list of keys, not a list of flags.
 *
 * Nothing here is optimistic, for the reason `use-flags.ts` sets out at length:
 * this is a control plane, and an unconfirmed value is worse than a brief
 * pending state. A creation is also the least optimistic write there is — the
 * id is permanent, and the two ways it fails (a taken id, a member that is not
 * an entitlement) are both things the server knows and the client does not.
 */

export const componentKeys = {
    all: ['components'] as const,
    list: () => [...componentKeys.all, 'list'] as const,
    /**
     * One museum's checklist for one component. Keyed by both ids because it is
     * a fact about the pair: the same component answers differently for every
     * museum, and the same museum differently for every component.
     */
    settings: (tenantId: string, componentId: string) =>
        [...componentKeys.all, 'settings', tenantId, componentId] as const,
    /**
     * Which components one museum holds. Keyed by the museum alone, unlike
     * {@link componentKeys.settings}: one answer covers the whole catalog, which
     * is the point of the route.
     */
    holdings: (tenantId: string) => [...componentKeys.all, 'holdings', tenantId] as const,
};

/**
 * The same retry policy the flags queries use, and deliberately the same
 * function shape: a 4xx is an answer rather than a blip, an expired session is
 * an answer too, and only a transport failure is worth one retry.
 */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useComponents() {
    return useQuery({
        queryKey: componentKeys.list(),
        queryFn: ({ signal }) => listComponents(signal),
        retry: retryUnlessClientError,
        // Load-bearing, as on every other query here: under the default
        // `networkMode: 'online'` a failure React Query reads as a connectivity
        // problem parks the query in `paused` rather than surfacing an error,
        // and a screen rendering a spinner on `isPending` would spin forever.
        networkMode: 'always',
    });
}

/**
 * Create a component, then refetch the list.
 *
 * `useFlagMutation`'s posture, minus the parts that have nothing to answer to
 * yet. The list is invalidated and the invalidation is **returned**, so the
 * mutation stays `isPending` until the refetch lands — otherwise the dialog
 * closes onto a table that does not yet show the row that was just created, and
 * the tab count above it disagrees with the rows below.
 *
 * There is no detail query to invalidate and no version-conflict branch: this
 * slice has one read, and creation is the only write. When editing a component
 * arrives it brings a version guard with it, and this should become the shared
 * mutation helper rather than grow a second copy of that reasoning.
 *
 * Never retried, for the reason flag writes are not: a retried create files a
 * second audit entry, and the second attempt would fail on the id the first one
 * took anyway.
 */
export function useCreateComponent() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateComponentInput) => createComponent(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: componentKeys.list() }),
        retry: false,
        networkMode: 'always',
    });
}

/* ------------------------------------------------- one museum's holding - */

/**
 * One museum's checklist for one component.
 *
 * Per component rather than per museum, because that is the route's shape: the
 * API answers one component's settings, and a tab showing six of them makes six
 * queries. That is the right trade here — each card can render as soon as its
 * own read lands, and a component whose read fails does not blank the other
 * five.
 *
 * Disabled until both ids are present. A museum id that resolves for nobody is
 * a real state on this screen, exactly as it is for `useTenantFlags`: the
 * clients list and the flags table are filled from different stores.
 */
/**
 * Which components this museum holds — one query for the whole tab.
 *
 * Per museum rather than per component, which is the opposite shape to
 * {@link useComponentSettings} next door and follows each route's own. A card
 * still renders before this lands: the union in `componentCard` treats an
 * absent answer as *not known yet*, never as *not held*, because a museum whose
 * flagless component is genuinely on would otherwise read as off for as long as
 * the query is in flight.
 */
export function useTenantComponents(tenantId: string) {
    return useQuery({
        queryKey: componentKeys.holdings(tenantId),
        queryFn: ({ signal }) => getTenantComponents(tenantId, signal),
        enabled: tenantId.length > 0,
        retry: retryUnlessClientError,
        networkMode: 'always',
    });
}

export function useComponentSettings(tenantId: string, componentId: string) {
    return useQuery({
        queryKey: componentKeys.settings(tenantId, componentId),
        queryFn: ({ signal }) => getComponentSettings(tenantId, componentId, signal),
        enabled: tenantId.length > 0 && componentId.length > 0,
        retry: retryUnlessClientError,
        networkMode: 'always',
    });
}

/**
 * The invalidation both activation writes share.
 *
 * **Both** key spaces, and the second one is the easy thing to forget: an
 * activation writes grants onto this museum's flag override rows, so the
 * Feature Flags tab next door is now showing a **Set by** column that predates
 * the write. An operator who activates a component and then switches tabs would
 * read an out-of-date answer to "who is holding this flag up" — and that column
 * exists precisely so they do not have to guess.
 *
 * Returned rather than `void`ed, as everywhere else in this app, so the mutation
 * stays `isPending` until the refetch lands: otherwise the dialog closes onto a
 * card still rendering the state it had before the click.
 */
function useActivationInvalidation(tenantId: string) {
    const queryClient = useQueryClient();

    return (componentId: string) =>
        Promise.all([
            queryClient.invalidateQueries({
                queryKey: componentKeys.settings(tenantId, componentId),
            }),
            queryClient.invalidateQueries({ queryKey: tenantFlagKeys.list(tenantId) }),
            // The third reading the card unions, and the only one that moves
            // for a component with no member flags: without this the card that
            // was just activated keeps rendering off, because neither of the
            // two above has anything to say about it.
            queryClient.invalidateQueries({ queryKey: componentKeys.holdings(tenantId) }),
        ]);
}

/**
 * Grant this museum a component.
 *
 * Not optimistic, for the reason `use-flags.ts` sets out at length: this is a
 * control plane, and a card that flipped to Active before the server agreed
 * would be showing an entitlement nobody has. It matters more here than for a
 * flag, because the write has a **success that is not a clean one** — a 200
 * carrying `COMPONENT_SETTINGS_SEED_INCOMPLETE` — and the caller reads that off
 * the returned {@link ActivationResult} rather than from the absence of an
 * error.
 *
 * Never retried automatically, even though the API is idempotent and its own 503
 * says retrying is safe: a retry files a second audit entry, and the operator is
 * the one who should decide to try again after reading what happened.
 */
export function useActivateComponent(tenantId: string) {
    const invalidate = useActivationInvalidation(tenantId);

    return useMutation<ActivationResult, Error, { componentId: string; reason?: string }>({
        mutationFn: (args) => activateComponent(args.componentId, tenantId, args.reason),
        onSuccess: (_result, args) => invalidate(args.componentId),
        retry: false,
        networkMode: 'always',
    });
}

/**
 * Withdraw a component from this museum.
 *
 * The same invalidation as activation and for the same reason — the grants come
 * off the museum's override rows, so the flags tab is stale either way. The
 * settings query is invalidated rather than dropped because deactivation
 * **keeps** the stored rows, and the refetched view is what tells the card it is
 * off-but-provisioned rather than off-and-empty.
 */
export function useDeactivateComponent(tenantId: string) {
    const invalidate = useActivationInvalidation(tenantId);

    return useMutation<void, Error, { componentId: string; reason?: string }>({
        mutationFn: (args) => deactivateComponent(args.componentId, tenantId, args.reason),
        onSuccess: (_result, args) => invalidate(args.componentId),
        retry: false,
        networkMode: 'always',
    });
}
